import { Component, EventEmitter, Input, OnInit, Output, NgZone, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
/// <reference types="google.maps" />
import { GoogleService } from '../../../service/googleService';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import constant from '../../../app.constant';
// Define interface for Google Places API
declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteService: new () => any;
          PlacesService: new (attributionNode: HTMLElement) => any;
          AutocompleteSessionToken: new () => any;
          PlacesServiceStatus: {
            OK: string;
            ZERO_RESULTS: string;
            OVER_QUERY_LIMIT: string;
            REQUEST_DENIED: string;
            INVALID_REQUEST: string;
            UNKNOWN_ERROR: string;
          };
        };
      };
    };
    [key: string]: any;
  }
}

@Component({
  selector: 'app-google-dropdown',
  templateUrl: './google-dropdown.component.html',
  styleUrls: ['./google-dropdown.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule
  ]
})
export class GoogleDropdownComponent implements OnInit {
  // Services
  private autocompleteService: any;
  private placesService: any;
  private sessionToken: any;

  // Data
  pickPlaces: any[] = [];
  placeDetails: any = null;

  // State
  private isGoogleApiLoaded = false;
  private isLoadingScript = false;

  // Inputs and Outputs
  @Input() childFormControlName: FormControl;
  @Input() id: any;
  @Input() readonly = false;
  @Output() lettersOnly = new EventEmitter<any>();
  @Output() checkAddressDetail = new EventEmitter<any>();
  @Output() placeDetailsEvent = new EventEmitter<any>();

  constructor(
    public googleService: GoogleService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initGooglePlacesApi();
  }

  /**
   * Initialize Google Places API
   */
  private initGooglePlacesApi(): void {
    // Check if API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      this.isGoogleApiLoaded = true;
      this.initServices();
      return;
    }

    // Prevent multiple script loads
    if (this.isLoadingScript) return;
    this.isLoadingScript = true;

    // Create callback function
    const callbackName = `googlePlacesCallback_${Date.now()}`;
    window[callbackName] = () => {
      this.ngZone.run(() => {
        console.log('Google Places API loaded');
        this.isGoogleApiLoaded = true;
        this.initServices();
        this.cdr.detectChanges();
      });
    };

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleService.apiKey}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;

    script.onload = () => console.log('Google Places script loaded');
    script.onerror = (error) => {
      console.error('Failed to load Google Places API:', error);
      this.isLoadingScript = false;
    };

    document.body.appendChild(script);
  }

  /**
   * Initialize Places services
   */
  private initServices(): void {
    if (!this.isGoogleApiLoaded) return;

    try {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
      this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      this.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      console.log('Google Places services initialized');
    } catch (error) {
      console.error('Error initializing Google Places services:', error);
    }
  }

  /**
   * Custom search function for ng-select
   */
  customSearchFn(term: string, item: any): boolean {
    // Disable ng-select's built-in filtering
    return true;
  }

  /**
   * Handle input changes in the dropdown
   */
  onInputChangePickup(event: any): void {
    // Clear results if input is empty
    if (!event?.term || event.term.length === 0) {
      this.pickPlaces = [];
      return;
    }

    // Only search on certain character counts to reduce API calls
    if ((event.term.length % 3 === 0) || (event.term.length === 1)) {
      // Initialize services if needed
      if (!this.autocompleteService) {
        this.initServices();
        if (!this.autocompleteService) return;
      }

      // Get predictions
      this.getPlacePredictions(event.term);
    }
  }

  /**
   * Get place predictions from Google Places API
   */
  private getPlacePredictions(query: string): void {
    if (!this.autocompleteService) return;

    const options = {
      input: query,
      sessionToken: this.sessionToken,
      componentRestrictions: { country: ['in', 'au', 'sg', 'lk'] },
      language: 'en'
    };
    if (constant.countryId == constant.countryIdList.India) {
      options.componentRestrictions = { country: ['in', 'au', 'sg', 'lk'] };
    } else if (constant.countryId == constant.countryIdList.Uae) {
      options.componentRestrictions = { country: ['ae', 'sa'] }
    } else if (constant.countryId == constant.countryIdList.AUS) {
      options.componentRestrictions = { country: ['au'] };
    } else if (constant.countryId == constant.countryIdList.SL) {
      options.componentRestrictions = { country: ['lk'] };
    } else {
      options.componentRestrictions = { country: ['in', 'au', 'ae', 'sg', 'lk'] };
    }
    try {
      this.autocompleteService.getPlacePredictions(options, (predictions: any[] | null, status: any) => {
        this.ngZone.run(() => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.pickPlaces = predictions;
          } else {
            this.pickPlaces = [];
          }
          this.cdr.detectChanges();
        });
      });
    } catch (error) {
      console.error('Error getting place predictions:', error);
      this.pickPlaces = [];
    }
  }

  /**
   * Get details for a selected place
   */
  getPlaceDetails(placeId: any): void {
    if (!placeId) return;

    // Initialize services if needed
    if (!this.placesService) {
      this.initServices();
      if (!this.placesService) return;
    }

    const request = {
      placeId: placeId?.place_id,
      sessionToken: this.sessionToken,
      fields: [
        'name',
        'formatted_address',
        'types',
        'geometry',
        'utc_offset_minutes',
        'url',
        'address_components',
        'place_id'
      ]
    };

    this.placesService.getDetails(request, (details: any, status: any) => {
      this.ngZone.run(() => {
        if (details && status === window.google.maps.places.PlacesServiceStatus.OK) {
          this.placeDetails = details;
          this.googleService.googleAddressLoad(this.placeDetails)
            .then(googleDetails => {
              this.placeDetailsEvent.emit(googleDetails);
              this.cdr.detectChanges();
            })
            .catch(error => console.error('Error loading address details:', error));
        } else {
          console.error('Error fetching place details:', status);
        }
      });
    });

    // Create a new session token for the next request
    if (window.google?.maps?.places) {
      this.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    }
  }

  /**
   * Event handlers
   */
  lettersOnlyClick(event: any): void {
    this.lettersOnly.emit(event);
  }

  checkAddressDetails(): void {
    this.checkAddressDetail.emit();
  }

  handleInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const allowedSymbols = "!@#$%^&*()_-+=[]{};:'\"<>,.?/";
    const pattern = `[^a-zA-Z0-9\\s${allowedSymbols.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`;
    const filteredInput = inputElement.value.replace(new RegExp(pattern, 'g'), '');
    inputElement.value = filteredInput;
  }
}
